import type { CreateVehicleInput, PowertrainType, TirePressureSet, Vehicle, VehicleStatus } from 'fuel-benchmark-contracts';
import { powertrainTypeSchema } from 'fuel-benchmark-contracts';
import type { StatusTone } from '../../components/ui/StatusText';

export const POWERTRAIN_TYPES = powertrainTypeSchema.options;

/**
 * Colour discipline (plan §2.2): the hybrid powertrain is the one distinction the vehicle list exists
 * to make visible, so it gets the calm blue. Every other powertrain stays muted.
 */
export const powertrainTone = (powertrainType: PowertrainType): StatusTone => (powertrainType === 'HYBRID' ? 'info' : 'muted');

/** ACTIVE is the expected state and stays muted; ARCHIVED is worth noticing. */
export const vehicleStatusTone = (status: VehicleStatus): StatusTone => (status === 'ARCHIVED' ? 'warning' : 'muted');

/** `Toyota Land Cruiser 300 Hybrid 2026` — the InfoCell title on the list and the detail heading. */
export const vehicleTitle = (vehicle: Pick<Vehicle, 'manufacturer' | 'model' | 'modelYear'>): string =>
  `${vehicle.manufacturer} ${vehicle.model} ${vehicle.modelYear}`;

/** `457 hp / 790 Nm` — the report's spec-card line (plan §8), reused on the Overview tab. */
export const powerOutput = (vehicle: Pick<Vehicle, 'powerHp' | 'torqueNm'>): string | null => {
  const parts = [vehicle.powerHp === null ? null : `${vehicle.powerHp} hp`, vehicle.torqueNm === null ? null : `${vehicle.torqueNm} Nm`].filter(
    (part): part is string => part !== null,
  );

  return parts.length > 0 ? parts.join(' / ') : null;
};

export const tirePressureSummary = (pressures: TirePressureSet | null): string | null =>
  pressures === null ? null : `${pressures.fl} / ${pressures.fr} / ${pressures.rl} / ${pressures.rr}`;

/** The single form shape both vehicle sheets use; the edit sheet locks `code` and drops it on submit. */
export type VehicleFormValues = CreateVehicleInput;

const orUndefined = (value: string | null): string | undefined => (value === null || value === '' ? undefined : value);

const orUndefinedNumber = (value: number | null): number | undefined => (value === null ? undefined : value);

/** Seeds the edit sheet from a saved vehicle. */
export const toVehicleFormValues = (vehicle: Vehicle): VehicleFormValues => ({
  code: vehicle.code,
  segmentId: vehicle.segmentId,
  manufacturer: vehicle.manufacturer,
  model: vehicle.model,
  modelYear: vehicle.modelYear,
  powertrainType: vehicle.powertrainType,
  trim: orUndefined(vehicle.trim),
  vin: orUndefined(vehicle.vin),
  engine: orUndefined(vehicle.engine),
  powerHp: orUndefinedNumber(vehicle.powerHp),
  torqueNm: orUndefinedNumber(vehicle.torqueNm),
  transmission: orUndefined(vehicle.transmission),
  drivetrain: orUndefined(vehicle.drivetrain),
  fuelType: orUndefined(vehicle.fuelType),
  defaultFuelGrade: orUndefined(vehicle.defaultFuelGrade),
  tireSize: orUndefined(vehicle.tireSize),
  oemTirePressureKpa: vehicle.oemTirePressureKpa ?? undefined,
  odometerKm: orUndefinedNumber(vehicle.odometerKm),
  notes: orUndefined(vehicle.notes),
});

export const EMPTY_VEHICLE_FORM_VALUES: Partial<VehicleFormValues> = {
  code: '',
  segmentId: '',
  manufacturer: '',
  model: '',
  powertrainType: 'ICE',
};

/**
 * The update contract clears a column on an explicit `null`, so every optional field the user emptied
 * is sent as `null` rather than dropped — otherwise clearing a trim in the UI would silently no-op.
 */
export const toUpdatePayload = (id: string, values: VehicleFormValues) => ({
  id,
  segmentId: values.segmentId,
  manufacturer: values.manufacturer,
  model: values.model,
  modelYear: values.modelYear,
  powertrainType: values.powertrainType,
  trim: values.trim ?? null,
  vin: values.vin ?? null,
  engine: values.engine ?? null,
  powerHp: values.powerHp ?? null,
  torqueNm: values.torqueNm ?? null,
  transmission: values.transmission ?? null,
  drivetrain: values.drivetrain ?? null,
  fuelType: values.fuelType ?? null,
  defaultFuelGrade: values.defaultFuelGrade ?? null,
  tireSize: values.tireSize ?? null,
  oemTirePressureKpa: values.oemTirePressureKpa ?? null,
  odometerKm: values.odometerKm ?? null,
  notes: values.notes ?? null,
});
